import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import {
  ChangeUserPasswordDialogComponent
} from '@components/dialog/change-user-password-dialog/change-user-password-dialog.component';
import {
  GeneratedPasswordDialogComponent
} from '@components/dialog/generated-password-dialog/generated-password-dialog.component';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { UserDetailDto, UserMergeDto, UserService } from '@artemis-software/wr-api';
import { PendingChanges } from '@guards/pending-changes.guard';
import {
  attachmentDetailToAttachmentReferenceDto,
} from '@shared/form-controls/attachment-upload/attachment-utils';
import { currentTenantId, currentUser } from '@/utils/admin-utils';
import { queryParam } from '@/utils/signals/router';

@Component({
  standalone: false,
  selector: 'wr-user-detail-page',
  templateUrl: './user-detail-page.component.html',
  styleUrls: ['./user-detail-page.component.scss'],
})
export class UserDetailPageComponent implements PendingChanges {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dialogService = inject(NbDialogService);
  private readonly userService = inject(UserService);
  private readonly toastrService = inject(NbToastrService);

  isEditing = false;
  firstSubmit = false;
  readonly userId = signal<string | null>(null);
  form?: FormGroup;

  readonly adminModeQueryParam = queryParam('mode', false);
  readonly isAdminMode = computed(() => this.adminModeQueryParam() === 'admin');
  readonly currentTenantId = currentTenantId();
  private readonly currentUser = currentUser();
  readonly currentUserLoadFailed = computed(() => this.currentUser() === null);
  readonly canEdit = signal(false);
  readonly isNew = computed(() => this.userId() === 'new');
  readonly loadedUser = signal<UserDetailDto | undefined>(undefined);
  readonly showQrPin = computed(() => {
    const user = this.loadedUser();
    if (!user || this.isNew() || user.tenantType !== UserDetailDto.TenantTypeEnum.External) {
      return undefined;
    }
    return this.canEdit() || this.currentUser()?.id === user.id ? user : undefined;
  });

  readonly selectedTenantId = signal<string>('');
  private readonly formRevision = signal(0);

  saving$ = new BehaviorSubject(false);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.userId.set(id);
      if (id) {
        if (id === 'new') {
          this.isEditing = true;
          this.canEdit.set(true);
          this.initForm(undefined, this.currentTenantId());
        } else {
          this.userService.findById(id).subscribe((user) => {
            if (this.isAdminMode()) {
              // canEditAdmin allein reicht nicht: merge und deleteById pruefen im Backend
              // EditUserPolicy (canEdit) - ohne das UND zeigte der Admin-Modus Buttons,
              // die der Server mit 403 beantwortet (WR-2499).
              this.canEdit.set(user.permissions.canEditAdmin && user.permissions.canEdit);
            } else {
              this.canEdit.set(user.permissions.canEdit);
            }
            this.loadedUser.set(user);
            this.initForm(user);
            this.form?.disable();
          });
        }
      } else {
        this.router.navigate(['users']);
      }
    });
    effect(() => {
      const tenantId = this.currentTenantId();
      // formRevision wird nur gelesen, damit der Effect erneut laeuft, sobald initForm eine
      // neue FormGroup gebaut hat - this.form selbst ist kein Signal und wuerde nicht tracken.
      this.formRevision();
      const tenantControl = this.form?.get('tenantId');
      if (tenantId && this.isNew() && tenantControl && !tenantControl.value) {
        tenantControl.setValue(tenantId);
      }
    });
  }

  initForm(dto?: UserDetailDto, tenantId?: string): void {
    let initialTenantId;
    if (tenantId) {
      initialTenantId = tenantId;
    } else {
      initialTenantId = dto?.tenant?.id;
    }

    if (initialTenantId) {
      this.selectedTenantId.set(initialTenantId);
    }

    this.form = new FormGroup({
      id: new FormControl(dto?.id),
      userName: new FormControl(dto?.userName, [Validators.required]),
      firstName: new FormControl(dto?.firstName, [Validators.required]),
      lastName: new FormControl(dto?.lastName, [Validators.required]),
      abbreviation: new FormControl(dto?.abbreviation, [Validators.required]),
      email: new FormControl(dto?.email, [Validators.required]),
      tenantId: new FormControl(initialTenantId, [Validators.required]),
      tenantName: new FormControl(dto?.tenant?.name),
      userProfilePicture: new FormControl(dto?.userProfilePicture),
      tenantRoleIds: new FormControl(dto?.roles?.map(role => role.id) ?? [], [Validators.required]),
      enableMaintenanceNotifications: new FormControl(dto?.enableMaintenanceNotifications),
      phoneNumber: new FormControl(dto?.phoneNumber),
      mobileNumber: new FormControl(dto?.mobileNumber),
    });

    this.form.get('tenantId')?.valueChanges.subscribe((newTenantId: string) => {
      if (newTenantId && newTenantId !== this.selectedTenantId()) {
        this.selectedTenantId.set(newTenantId);
        this.form?.get('tenantRoleIds')?.setValue([]);
      }
    });
    this.formRevision.update(revision => revision + 1);
  }

  hasError(controlName: string, errorName = 'required'): boolean {
    const control = this.form?.get(controlName);
    return this.isEditing && this.firstSubmit && (control?.hasError(errorName) ?? false) && ((control?.touched ?? false) || (control?.dirty ?? false));
  }

  onEdit(): void {
    if (this.isEditing) {
      this.isEditing = false;
      this.form?.disable();
    } else {
      this.isEditing = true;
      this.form?.enable();
    }
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAllAsTouched();
    this.form?.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    if (!this.form?.valid) {
      this.toastrService.danger('Benutzer kann nicht gespeichert werden, es gibt noch Fehler.', 'Benutzer');
      return;
    }

    if (this.form?.value.tenantId === '') {
      this.form?.addControl('tenantId', new FormControl(undefined));
    }
    try {
      this.saving$.next(true);
      const userDto: UserMergeDto = {
        ...this.form?.value,
        userProfilePicture: attachmentDetailToAttachmentReferenceDto(this.form?.value.userProfilePicture),
      };

      if (!this.isAdminMode()) {
        userDto.tenantId = this.currentTenantId();
      }

      if (this.isNew()) {
        const result = await firstValueFrom(this.userService.createWithPassword(userDto));
        this.isEditing = false;
        this.showGeneratedPassword(result.generatedPassword);
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['user', result.user.id], { replaceUrl: true });
        });
      } else {
        const user = await firstValueFrom(this.userService.merge(userDto));
        this.isEditing = false;
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          this.router.navigate(['user', user.id], { replaceUrl: true }).then(() => {
            this.toastrService.success('Benutzer wurde erfolgreich gespeichert', 'Benutzer');
          });
        });
      }
    } catch (e: unknown) {
      console.error(e);
      this.toastrService.danger('Benutzer konnte nicht gespeichert werden', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  private showGeneratedPassword(password: string): void {
    this.dialogService.open(GeneratedPasswordDialogComponent, {
      context: { password },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });
  }

  openChangePasswordDialog(): void {
    const userId = this.userId();
    if (!userId || userId === 'new') return;
    this.dialogService.open(ChangeUserPasswordDialogComponent, {
      context: { mode: 'admin', userId },
    }).onClose.subscribe(success => {
      if (success) {
        this.toastrService.success('Passwort wurde erfolgreich zurückgesetzt', 'Benutzer');
      }
    });
  }

  onDelete(): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Benutzer löschen',
        message: 'Bist du sicher, dass du den Benutzer löschen willst?',
      },
    }).onClose.subscribe((result) => {
      if (result) {
        // Der error-Handler gehoert an den HTTP-Call, nicht an den Dialog: seit WR-2499 sind
        // 400 (Selbstloeschung) und 403 (EditUserPolicy) erreichbar, und ein Handler am
        // onClose-Subscribe saehe sie nie.
        this.userService.deleteById(this.form?.value.id).subscribe({
          next: () => {
            if (this.isAdminMode()) {
              this.router.navigate(['adminUsers']);
            } else {
              this.router.navigate(['tenantUsers']);
            }
            this.toastrService.success('Benutzer wurde erfolgreich gelöscht', 'Benutzer');
          },
          error: () => {
            this.toastrService.danger('Beim Löschen des Benutzers ist ein Fehler aufgetreten', 'Benutzer');
          },
        });
      }
    });
  }

  async cancel(): Promise<void> {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['user', this.form?.value.id]);
    });
  }

  async navigateBack(): Promise<void> {
    if (this.isAdminMode()) {
      await this.router.navigate(['adminUsers']);
    } else {
      await this.router.navigate(['tenantUsers']);
    }
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }
}
